import React, { ForwardRefRenderFunction, forwardRef } from "react";
//#region redux
import { useSelector } from "react-redux";
import { AppState } from "store";
//#endregion
//#region types
import { ReceiptFormTypes } from "models/formTypes";
import { FormHandles } from "models/propTypes/genericFormProps";
import { TranslationKeys } from "translations";
import { DimensionEnums } from "models/enums";
import { ReceiptDraftTodoSelectionFormContainerProps } from "models/propTypes/receiptPropTypes";
import { WintSuperkollAPIModel } from "wint-api-client/dist/types";
import { FormikProps } from "formik";
import { AutocompleteListItem } from "models/propTypes/listPropTypes";
import { FormFieldConstants, ServiceConstants } from "utils/constants";
import { UploadedFile } from "models/utilTypes/fileUtilsTypes";
import { ReceiptUploadedFile } from "models/formTypes/receipt";
//#endregion
//#region components
import ReceiptDraftTodoSelectionForm from "./todoSelectionForm";
import useReceiptDraftFormValidations from "../useReceiptDraftFormValidations";
import { useCreateFormHelpers } from "components/documents";
import { useDimensionMaps } from "components/common";
import { useDocumentDraftService } from "serviceHooks";
import {
  CostForwardingFormHandleSync,
  CostForwardingMarkingProvider,
  useReceiptTodoCostForwarding,
} from "components/costForwardingMarking";
//#endregion
import { DimensionHelper, LocalizationHelperInstance } from "utils/helpers";
import { ReceiptService } from "services";

// The receipt from the API carries no image data; data already downloaded for an image id is reused
// so a reinitialise after a save does not blank the thumbnails.
const toUploadedFiles = (
  attachments?: WintSuperkollAPIModel.ReceiptAttachment[],
  getLoadedData?: (attachmentId: number) => string | undefined
) =>
  attachments?.map<UploadedFile>(
    (attachment): UploadedFile => ({
      ...attachment,
      contentType: attachment.contentType ?? "",
      fileName: attachment.fileName ?? "",
      id: attachment.id,
      fileSize: 0,
      data: attachment.data || getLoadedData?.(attachment.id) || "",
    })
  );

const ReceiptDraftTodoSelectionFormContainer: ForwardRefRenderFunction<
  FormHandles,
  ReceiptDraftTodoSelectionFormContainerProps
> = (props, ref) => {
  const documentDraftService = useDocumentDraftService();
  const formRef = React.useRef<FormHandles>(null);
  React.useImperativeHandle(ref, () => (formRef?.current ? formRef.current : ({} as FormHandles)));

  const {
    receiptCategories,
    receiptPaymentMethods,
    topReceiptPaymentMethods,
    receiptPaymentMethodActions,
    setIsReceiptLoading,
  } = useCreateFormHelpers();

  const { currentUser, selectedCompany } = useSelector(
    (store: AppState) => ({
      currentUser: store.auth.loggedInUser.name,
      selectedCompany: store.auth.selectedCompany,
    }),
    (left, right) => left.currentUser !== right.currentUser && left.selectedCompany.id !== right.selectedCompany.id
  );

  const { receiptDraftValidationScheme } = useReceiptDraftFormValidations();

  const { receipt, onReceiptCategoryChange } = props;

  const { dimensions } = useDimensionMaps(DimensionEnums.DimensionCategory.Receipt);

  const [currency, setCurrency] = React.useState<string>(receipt.currency ?? "");
  const [dimensionAmount, setDimensionAmount] = React.useState<number>(receipt.amount);

  const [documentDrafts, setDocumentDrafts] = React.useState<AutocompleteListItem[]>([]);

  React.useEffect(() => {
    const fetchDocumentDrafts = async () => {
      const [filterRes, handledDocDraft] = await Promise.all([
        documentDraftService.filter({
          ...ServiceConstants.DefaultFilter,
          states: [WintSuperkollAPIModel.DocumentDraftState.Created],
          orderByProperty: "Created",
          orderByDescending: true,
          numPerPage: 100,
        }),
        receipt.documentDraftId && receipt.isMatchedToDocumentDraftViaAutomationService
          ? documentDraftService.get(receipt.documentDraftId)
          : Promise.resolve(null),
      ]);

      const items = (filterRes.data.items ?? []).map(
        (i): AutocompleteListItem => ({
          id: i.id,
          value: i.subject ?? "",
          description: i.from,
          data: i,
          group: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Suggestions),
        })
      );

      if (receipt.documentDraftId && receipt.isMatchedToDocumentDraftViaAutomationService && handledDocDraft) {
        items.push({
          id: handledDocDraft.id,
          value: handledDocDraft.subject ?? "",
          description: handledDocDraft.from,
          data: handledDocDraft,
          group: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Suggestions),
          hide: (options: AutocompleteListItem[], value?: string, isValueFromOptions?: boolean) => true,
        });
      }

      setDocumentDrafts(items);
    };

    fetchDocumentDrafts();
  }, []);

  const { pendingDocumentDraft, onPendingDocumentDraftApplied, getLoadedImageData } = props;

  // Computed once per receipt, never per render: Formik reinitialises whenever initialValues stop
  // being deep-equal, so image data arriving on an unrelated re-render (e.g. after removing an
  // image) would reset the form and bring the removed image back.
  const receiptFiles = React.useMemo(() => toUploadedFiles(receipt.attachments, getLoadedImageData), [receipt]);

  const setDocumentDraftName = (documentDraftId?: number) => {
    formRef.current?.setFieldValue?.(
      FormFieldConstants.Receipt.ReceiptDraftTodo.DocumentDraft,
      documentDraftId ? documentDrafts.find((d) => d.id === documentDraftId)?.value : undefined
    );
  };

  // Keyed on the receipt object, not its id: a save reinitialises the form with the same id and
  // clears the name.
  React.useEffect(() => {
    receipt.documentDraftId && documentDrafts.length > 0 && setDocumentDraftName(receipt.documentDraftId);
  }, [receipt, documentDrafts]);

  // A save never links the underlag. Once the saved receipt has arrived, put the unlinked pick back
  // on top of it, then let the todo forget it so a later reload cannot bring it back. This runs after
  // Formik's own reinitialise (child effects run first). The receipt's own images come from the saved
  // receipt, not the form, because Core gives them new ids when a save sends images.
  React.useEffect(() => {
    const form = formRef.current;
    if (!pendingDocumentDraft || !form?.setFieldValue || pendingDocumentDraft.savedFrom === receipt) return;

    form.setFieldValue(
      FormFieldConstants.Receipt.ReceiptDraftTodo.DocumentDraftId,
      pendingDocumentDraft.documentDraftId
    );
    form.setFieldValue(FormFieldConstants.Receipt.ReceiptDraftTodo.FilesToUpload, [
      ...(receiptFiles ?? []),
      ...pendingDocumentDraft.files,
    ]);
    setDocumentDraftName(pendingDocumentDraft.documentDraftId);
    onPendingDocumentDraftApplied?.();
  }, [receipt, pendingDocumentDraft]);

  React.useEffect(() => {
    const paymentMethod =
      topReceiptPaymentMethods.length > 0
        ? topReceiptPaymentMethods[0]
        : receiptPaymentMethods.length === 1
          ? receiptPaymentMethods[0]
          : undefined;

    if (!paymentMethod) return;

    if (formRef?.current?.setFieldValue) {
      formRef.current.setFieldValue("receipt.paymentMethod", Number(paymentMethod.id));
    }
  }, [topReceiptPaymentMethods]);

  React.useEffect(() => {
    if (selectedCompany.receiptCreator)
      ReceiptService.getReceiptsCanBePayedOut()
        .then((res) => {
          formRef?.current?.setFieldValue && formRef.current.setFieldValue("receipt.canBePayedOut", res.data);
        })
        .finally(() => {
          setIsReceiptLoading(false);
        });
    else {
      setIsReceiptLoading(false);
    }
  }, [currentUser]);

  const onReceiptCategorySelect = (
    item: AutocompleteListItem,
    name: string,
    formikProps: FormikProps<ReceiptFormTypes.ReceiptDraftTodo>
  ) => {
    const { setFieldValue } = formikProps;
    setFieldValue(
      FormFieldConstants.Document.Create.ReceiptCategoryType,
      (item.data as WintSuperkollAPIModel.ReceiptCategory).categoryType
    );
    onReceiptCategoryChange && onReceiptCategoryChange(item);
  };

  const onReceiptCategoryClear = (name: string, formikProps: FormikProps<ReceiptFormTypes.ReceiptDraftTodo>) => {
    const { setFieldValue } = formikProps;
    setFieldValue(FormFieldConstants.Document.Create.ReceiptCategoryType, undefined);
    onReceiptCategoryChange && onReceiptCategoryChange(undefined);
  };

  const onFilesUploadChange = (
    name: string,
    updatedValue: ReceiptUploadedFile[],
    formikProps: FormikProps<ReceiptFormTypes.ReceiptDraftTodo>
  ) => {
    const { values, setFieldValue } = formikProps;

    if (!values.filesToUpload?.some((a) => a.isDocumentDraft)) {
      (!receipt.isMatchedToDocumentDraftViaAutomationService || receipt.documentDraftId !== values.documentDraftId) &&
        setFieldValue(FormFieldConstants.Receipt.ReceiptDraftTodo.DocumentDraft, undefined);
    }
    props.onFilesUploadChange && props.onFilesUploadChange(name, updatedValue, formikProps);

    automaticMatchedAttachementChanged(updatedValue, values) &&
      props.toggleRuleCreateButton &&
      props.toggleRuleCreateButton(false);
  };

  const automaticMatchedAttachementChanged = (
    updatedValue: ReceiptUploadedFile[],
    values: ReceiptFormTypes.ReceiptDraftTodo
  ) =>
    receipt.isMatchedToDocumentDraftViaAutomationService &&
    (values.documentDraftId !== receipt.documentDraftId || updatedValue.length !== (receipt.attachments?.length ?? 0));

  const onDocumentDraftSelect = (
    item: AutocompleteListItem,
    name: string,
    formikProps: FormikProps<ReceiptFormTypes.ReceiptDraftTodo>
  ) => {
    const documentDraft = item.data as WintSuperkollAPIModel.DocumentDraft;

    if (documentDraft.attachments) {
      Promise.all(
        documentDraft.attachments?.map((attachment) => {
          return documentDraftService.getAttachment(documentDraft.id, attachment.id);
        })
      ).then((val) => {
        const { values } = formikProps;
        const filteredAttachments = values.filesToUpload?.filter((a) => !a.isDocumentDraft) ?? [];
        const documentDrafts = val.map(
          (v) =>
            ({
              id: v.id,
              contentType: v.contentType ?? "",
              data: v.data ?? "",
              fileName: "",
              isDocumentDraft: true,
            }) as ReceiptFormTypes.ReceiptUploadedFile
        );
        const updatedAttachments = [...filteredAttachments, ...documentDrafts];
        formRef.current?.setFieldValue &&
          formRef.current.setFieldValue(FormFieldConstants.Receipt.ReceiptDraftTodo.FilesToUpload, updatedAttachments);
      });
    }
  };

  const onDocumentDraftChanged = (
    name: string,
    updatedValue: any,
    formikProps: FormikProps<ReceiptFormTypes.ReceiptDraftTodo>,
    userInput?: boolean
  ) => {
    const { values } = formikProps;

    receipt.isMatchedToDocumentDraftViaAutomationService &&
      values.documentDraftId !== receipt.documentDraftId &&
      props.toggleRuleCreateButton &&
      props.toggleRuleCreateButton(false);

    if (values.documentDraftId) {
      return;
    }

    const filteredAttachments = values.filesToUpload?.filter((a) => !a.isDocumentDraft) ?? [];
    formRef.current?.setFieldValue &&
      formRef.current.setFieldValue(FormFieldConstants.Receipt.ReceiptDraftTodo.FilesToUpload, filteredAttachments);
  };

  const onAmountsChange = (name: string, value: any, formikProps: FormikProps<ReceiptFormTypes.Todo>) => {
    setDimensionAmount(value);
  };

  const onCurrencyChange = (name: string, value: any, formikProps: FormikProps<ReceiptFormTypes.Todo>) => {
    setCurrency(value);
  };

  const { costForwardingContext, latestCostForwardingValues } = useReceiptTodoCostForwarding(receipt, () =>
    formRef.current?.getValues?.()
  );

  const receiptCategoryType = receiptCategories.find((r) => r.id === receipt.receiptCategoryId)?.data.categoryType;

  const initialValues: ReceiptFormTypes.ReceiptDraftTodo = React.useMemo(
    () => ({
      amount: receipt.amount,
      currency: receipt.currency,
      paymentMethod: receipt.paymentMethodName,
      paymentMethodId: receipt.paymentMethodId,
      receiptCategory: receipt.categoryName,
      receiptCategoryId: receipt.receiptCategoryId,
      dimensions: dimensions ? DimensionHelper.getValidList(receipt.dimensionList ?? [], dimensions) : [],
      showDimensions: dimensions && dimensions.length > 0,
      documentDraftId: receipt.documentDraftId,
      receiptCategoryType,
      receiptPurpose: receipt.intent,
      // The API stores the Other explanation in intent too (getUpdatedReceiptDraft); without this a
      // reinitialise after a save blanks the field.
      otherExplanation:
        receiptCategoryType === WintSuperkollAPIModel.ReceiptCategoryType.Other ? receipt.intent : undefined,
      receiptPersons:
        receipt.persons?.map((p) => ({
          id: p.id,
          name: p.name ?? "",
          company: p.company ?? "",
        })) ?? [],
      filesToUpload: receiptFiles,
      ...latestCostForwardingValues.current,
    }),
    [receipt, receiptFiles, dimensions, receiptCategoryType]
  );

  return (
    <CostForwardingMarkingProvider value={costForwardingContext}>
      <ReceiptDraftTodoSelectionForm
        ref={formRef}
        {...props}
        validationSchema={receiptDraftValidationScheme()}
        defaultValues={initialValues}
        receiptCategories={receiptCategories}
        receiptPaymentMethods={receiptPaymentMethods}
        topReceiptPaymentMethods={topReceiptPaymentMethods}
        receiptPaymentMethodActions={receiptPaymentMethodActions}
        dimensionAmount={dimensionAmount}
        currency={currency}
        onFilesUploadChange={onFilesUploadChange}
        documentDrafts={documentDrafts}
        onDocumentDraftSelect={onDocumentDraftSelect}
        receiptCategorySelect={onReceiptCategorySelect}
        receiptCategoryClear={onReceiptCategoryClear}
        onDocumentDraftChanged={onDocumentDraftChanged}
        amountsChangeHandler={onAmountsChange}
        currencyChangeHandler={onCurrencyChange}
      />
      <CostForwardingFormHandleSync formRef={formRef} />
    </CostForwardingMarkingProvider>
  );
};

export default forwardRef(ReceiptDraftTodoSelectionFormContainer);
